import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loader, Button } from "@/components/PageUI";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserRound, Plus, Mail, CreditCard, Link2, Upload, Download } from "lucide-react";
import DobField from "@/components/DobField";
import AvatarUpload from "@/components/AvatarUpload";

const empty = {
  name: "", team_id: "", photo: "", dob: "", jersey_number: "", position: "",
  parent_name: "", parent_email: "", parent_phone: "", medical_notes: "",
  emergency_contact: "", id_number: "", awards: [], documents: [],
};

const ageFrom = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1;
  return a;
};

export default function Players() {
  const { sportMeta } = useAuth();
  const [players, setPlayers] = useState(null);
  const [covered, setCovered] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState("all");
  const [cardFilter, setCardFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const navigate = useNavigate();

  const load = () => api.get("/players").then((r) => setPlayers(r.data)).catch(() => setPlayers([]));
  const invitePlayer = async (e, p) => {
    e.stopPropagation();
    if (!p.parent_email) return toast.error("Add an email to this athlete first");
    try { await api.post(`/players/${p.id}/invite`); toast.success(`Portal invite sent to ${p.parent_email}`); }
    catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d && !String(d).includes("<") ? d : "Could not send the invite email right now. Please try again.");
    }
  };
  const copyInviteLink = async (e, p) => {
    e.stopPropagation();
    if (!p.parent_email) return toast.error("Add an email to this athlete first");
    try {
      const { data } = await api.get(`/players/${p.id}/invite-link`);
      await navigator.clipboard.writeText(data.link);
      toast.success("Invite link copied — share it via WhatsApp or SMS");
    } catch (err) { toast.error(apiErr(err, "Could not create the invite link")); }
  };
  const [asking, setAsking] = useState(false);
  const askForCards = async () => {
    setAsking(true);
    try {
      const { data } = await api.post("/fees/request-cards");
      if (!data.targeted) toast.success("Everyone with an email already has a card on file 🎉");
      else if (data.sent) toast.success(`Asked ${data.sent} famil${data.sent === 1 ? "y" : "ies"} to add a card`);
      else toast.error("Couldn't send the emails right now — please try again shortly");
    } catch (e) { toast.error(apiErr(e, "Could not send the requests")); }
    setAsking(false);
  };
  useEffect(() => {
    load();
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/fees/card-coverage").then((r) => setCovered(r.data.covered)).catch(() => {});
  }, []);

  const onPhoto = (url) => { if (url) setForm((f) => ({ ...f, photo: url })); };

  const create = async () => {
    if (!form.name || !form.team_id) return toast.error("Name and team are required");
    try {
      await api.post("/players", form);
      toast.success("Athlete added"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const [importOpen, setImportOpen] = useState(false);
  const [importTeam, setImportTeam] = useState("");
  const [importText, setImportText] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const onCsv = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader(); reader.onload = () => { setImportText(String(reader.result || "")); setImportResult(null); }; reader.readAsText(file);
  };
  const doImport = async () => {
    if (!importTeam) return toast.error("Pick a team first");
    if (!importText.trim()) return toast.error("Choose a CSV file");
    setImportBusy(true);
    try {
      const { data } = await api.post("/players/import", { team_id: importTeam, csv: importText });
      setImportResult(data);
      toast.success(`Imported ${data.created} athlete${data.created === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setImportBusy(false); }
  };
  const csvTemplate = "data:text/csv;charset=utf-8," + encodeURIComponent("name,jersey_number,position,dob,parent_name,parent_email,parent_phone\nAlex Smith,10,Forward,2012-05-01,Jane Smith,jane@email.com,555-1234\n");

  const shown = players?.filter((p) =>
    (filter === "all" || p.team_id === filter) &&
    (cardFilter === "all" || !covered.includes(p.id))
  ) || [];
  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";
  const positions = sportMeta?.positions || [];
  const noCardCount = (players || []).filter((p) => !covered.includes(p.id)).length;

  if (!players) return <Loader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Athletes" subtitle={`Profiles, medical info, awards & documents.`} testid="players-header"
        action={
          <div className="flex gap-2">
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={!teams.length} data-testid="import-csv-btn"><Upload size={16} className="mr-1.5" /> Import CSV</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="import-dialog">
              <DialogHeader><DialogTitle>Import athletes from CSV</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <p className="text-sm text-muted-foreground">Upload a CSV with columns like <span className="font-mono text-xs">name, jersey_number, position, dob, parent_name, parent_email, parent_phone</span>. Only <b>name</b> is required. <a href={csvTemplate} download="athletes-template.csv" className="text-primary underline" data-testid="csv-template-link">Download template</a>.</p>
                <div><Label>Team *</Label>
                  <Select value={importTeam} onValueChange={setImportTeam}>
                    <SelectTrigger className="mt-1.5" data-testid="import-team-select"><SelectValue placeholder="Select team" /></SelectTrigger>
                    <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>CSV file</Label><Input type="file" accept=".csv,text/csv" onChange={onCsv} className="mt-1.5" data-testid="import-file-input" /></div>
                {importResult && (
                  <div className="rounded-lg border border-border p-3 text-sm" data-testid="import-result">
                    <p className="font-medium text-emerald-500">Imported {importResult.created} athlete(s).</p>
                    {importResult.errors?.length > 0 && <ul className="mt-1 text-xs text-muted-foreground list-disc pl-4">{importResult.errors.map((er, i) => <li key={i}>{er}</li>)}</ul>}
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={doImport} disabled={importBusy} className="brand-gradient text-white border-0" data-testid="do-import-btn">{importBusy ? "Importing..." : "Import athletes"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="brand-gradient text-white border-0 hover:opacity-90" disabled={!teams.length} data-testid="add-player-btn"><Plus size={16} className="mr-1.5" /> Add athlete</Button>
            </DialogTrigger>
            <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl" data-testid="player-dialog">
              <DialogHeader><DialogTitle>New athlete</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <AvatarUpload value={form.photo} onFile={onPhoto} testid="player-photo-input" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" data-testid="player-name-input" /></div>
                  <div><Label>Team *</Label>
                    <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="player-team-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date of birth</Label><DobField value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testid="player-dob" />{ageFrom(form.dob) !== null && <p className="text-xs text-muted-foreground mt-1" data-testid="player-age-hint">Age {ageFrom(form.dob)}</p>}</div>
                  <div><Label>Jersey #</Label><Input value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} className="mt-1.5 h-11" /></div>
                  <div className="sm:col-span-2"><Label>Position</Label>
                    {positions.length ? (
                      <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                        <SelectTrigger className="mt-1.5 h-11" data-testid="player-position-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="mt-1.5 h-11" />}
                  </div>
                  <div><Label>ID number</Label><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className="mt-1.5 h-11" /></div>
                  {ageFrom(form.dob) !== null && ageFrom(form.dob) >= 18 ? (
                    <>
                      <div><Label>Athlete email</Label><Input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="mt-1.5 h-11" data-testid="player-email-input" /></div>
                      <div><Label>Athlete phone</Label><Input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className="mt-1.5 h-11" data-testid="player-phone-input" /></div>
                    </>
                  ) : (
                    <>
                      <div><Label>Parent name</Label><Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="mt-1.5 h-11" data-testid="player-parent-name-input" /></div>
                      <div><Label>Parent email</Label><Input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="mt-1.5 h-11" data-testid="player-parent-email-input" /></div>
                      <div className="sm:col-span-2"><Label>Parent phone</Label><Input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className="mt-1.5 h-11" data-testid="player-parent-phone-input" /></div>
                    </>
                  )}
                  <div className="sm:col-span-2"><Label>Emergency contact</Label><Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} className="mt-1.5 h-11" /></div>
                  <div className="sm:col-span-2"><Label>Medical notes</Label><Textarea value={form.medical_notes} onChange={(e) => setForm({ ...form, medical_notes: e.target.value })} className="mt-1.5" rows={3} /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="w-full sm:w-auto h-11 brand-gradient text-white border-0" data-testid="save-player-btn">Add athlete</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        } />

      {players.length > 0 && (
        <div className="mb-5 rounded-2xl border border-border bg-card p-4" data-testid="coverage-summary">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-sm font-medium"><span data-testid="coverage-count" className="text-secondary font-bold">{players.length - noCardCount}</span> of {players.length} athletes covered</p>
            <p className="text-xs text-muted-foreground">{Math.round(((players.length - noCardCount) / players.length) * 100)}% have a card on file</p>
          </div>
          <div className="h-2 rounded-full bg-accent overflow-hidden">
            <div className="h-full brand-gradient rounded-full transition-all duration-500" style={{ width: `${Math.round(((players.length - noCardCount) / players.length) * 100)}%` }} />
          </div>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={() => { const rows = [["name", "email", "phone", "team"]].concat(players.map((p) => [p.name || "", p.parent_email || "", p.parent_phone || "", teamName(p.team_id)])); const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n"); const a = document.createElement("a"); a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv); a.download = "athletes.csv"; a.click(); toast.success("Athlete list exported"); }} data-testid="export-emails-btn"><Download size={15} className="mr-1.5" /> Export CSV</Button>
        <div className="w-52">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="players-team-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="inline-flex rounded-lg border border-border p-1 bg-card">
          <button type="button" onClick={() => setCardFilter("all")} data-testid="card-filter-all"
            className={`h-8 px-3 rounded-md text-sm font-medium transition-colors duration-150 ${cardFilter === "all" ? "brand-gradient text-white" : "text-muted-foreground hover:text-foreground"}`}>All athletes</button>
          <button type="button" onClick={() => setCardFilter("nocard")} data-testid="card-filter-nocard"
            className={`h-8 px-3 rounded-md text-sm font-medium transition-colors duration-150 ${cardFilter === "nocard" ? "brand-gradient text-white" : "text-muted-foreground hover:text-foreground"}`}>
            No card on file{noCardCount ? ` (${noCardCount})` : ""}
          </button>
        </div>
        {noCardCount > 0 && (
          <Button variant="outline" size="sm" onClick={askForCards} disabled={asking} className="h-9" data-testid="ask-for-card-btn">
            <Mail size={15} className="mr-1.5" /> {asking ? "Sending…" : "Ask for a card"}
          </Button>
        )}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={UserRound} title={cardFilter === "nocard" ? "Everyone has a card on file 🎉" : "No athletes yet"} desc={cardFilter === "nocard" ? "No athletes are missing a saved card right now." : (teams.length ? "Add your first athlete." : "Create a team first, then add athletes.")} testid="empty-players" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {shown.map((p) => (
            <button key={p.id} onClick={() => navigate(`/app/players/${p.id}`)}
              className="text-left rounded-2xl border border-border bg-card p-5 hover:-translate-y-1 transition-transform duration-200" data-testid={`player-card-${p.id}`}>
              <div className="flex items-center gap-3">
                {p.photo ? <img src={p.photo} alt="" className="h-12 w-12 rounded-full object-cover border border-border" />
                  : <div className="h-12 w-12 rounded-full brand-gradient flex items-center justify-center text-white font-semibold">{p.name[0]}</div>}
                <div className="min-w-0">
                  <p className="font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{teamName(p.team_id)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
                {p.jersey_number && <span className="bg-accent px-2 py-0.5 rounded">#{p.jersey_number}</span>}
                {p.position && <span>{p.position}</span>}
                <span className={`inline-flex items-center gap-1 ${covered.includes(p.id) ? "text-secondary" : "text-muted-foreground/60"}`} data-testid={`card-badge-${p.id}`} title={covered.includes(p.id) ? "Card saved — Charge now works for this athlete" : "No saved card on file"}>
                  <CreditCard size={13} /> {covered.includes(p.id) ? "Card ✓" : "Card ✗"}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs">
                <span role="button" tabIndex={0} onClick={(e) => invitePlayer(e, p)}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  data-testid={`invite-player-${p.id}`}>
                  <Mail size={13} /> Invite
                </span>
                <span role="button" tabIndex={0} onClick={(e) => copyInviteLink(e, p)}
                  className="inline-flex items-center gap-1 text-secondary hover:underline"
                  data-testid={`copy-invite-${p.id}`}>
                  <Link2 size={13} /> Copy link
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
